import React, { useCallback } from "react"

import {
  Button,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@coval/ui"
import { GripVertical, Trash2 } from "lucide-react"

import type { TraceFilterEntryConfig } from "@/types/metrics/metadata"

import {
  createDefaultTraceFilterEntry,
  TRACE_FILTER_TYPES,
  type TraceFilterEntryType,
} from "../types"
import { SpanAttributeFilterEditor } from "./SpanAttributeFilterEditor"
import { SpanNameFilterEditor } from "./SpanNameFilterEditor"
import { StatusFilterEditor } from "./StatusFilterEditor"
import { TimeRangeFilterEditor } from "./TimeRangeFilterEditor"
import { TurnRangeFilterEditor } from "./TurnRangeFilterEditor"

interface TraceFilterRowProps {
  filter: TraceFilterEntryConfig
  onChange: (filter: TraceFilterEntryConfig) => void
  onRemove: () => void
}

export const TraceFilterRow: React.FC<TraceFilterRowProps> = ({
  filter,
  onChange,
  onRemove,
}) => {
  const handleTypeChange = useCallback(
    (value: string) => {
      const newType = value as TraceFilterEntryType
      if (newType === filter.type) {
        return
      }
      onChange(createDefaultTraceFilterEntry(newType))
    },
    [filter.type, onChange],
  )

  return (
    <div className="flex items-center gap-2">
      <GripVertical
        className="w-4 h-4 text-coval-text-muted cursor-grab active:cursor-grabbing shrink-0"
        aria-hidden
      />
      <div className="flex-1 flex items-center gap-3">
        <Select value={filter.type} onValueChange={handleTypeChange}>
          <SelectTrigger className="w-40" aria-label="Trace filter type">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {TRACE_FILTER_TYPES.map((type) => (
              <SelectItem key={type.value} value={type.value}>
                {type.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <div className="flex-1">
          {filter.type === "span_name" && (
            <SpanNameFilterEditor filter={filter} onChange={onChange} />
          )}
          {filter.type === "span_attribute" && (
            <SpanAttributeFilterEditor filter={filter} onChange={onChange} />
          )}
          {filter.type === "time_range" && (
            <TimeRangeFilterEditor filter={filter} onChange={onChange} />
          )}
          {filter.type === "turn_range" && (
            <TurnRangeFilterEditor filter={filter} onChange={onChange} />
          )}
          {filter.type === "status" && (
            <StatusFilterEditor filter={filter} onChange={onChange} />
          )}
        </div>
      </div>

      <Button
        variant="ghost"
        icon
        onClick={onRemove}
        tooltip="Remove trace filter"
        className="text-coval-text-muted hover:text-coval-danger-text"
      >
        <Trash2 />
      </Button>
    </div>
  )
}
